import moment from 'moment';
import { useCallback } from 'react';
import {
  FetchableResourceType,
  ResourceType,
} from 'api/resource-management/types';
import { EventDeleteParams } from 'components/organisms/resource-management/scheduler-config/listeners';
import { bulkActionsMapActions } from 'components/organisms/resource-management/utils/bulk-actions';
import { asAllocationPayload } from 'components/organisms/resource-management/utils/events-conversion/common';
import { useAllocationAndSummaryManager } from '../../resource-summary-manager';
import { ExtendedManagerProps } from '../../types';
import { useTotalAllocationInUIManager } from '../../ui-total-allocation-manager';

type ReturnProps = (params: EventDeleteParams) => Promise<void>;

export const useAllocationDeleteHandler = (
  props: ExtendedManagerProps,
): ReturnProps => {
  const { schedulerRef, bulkUpdate } = props;

  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);
  const { updateTotalAllocation } = useTotalAllocationInUIManager(props);

  return useCallback(
    ({ event: allocationEvent }: EventDeleteParams) => {
      if (allocationEvent == null) {
        return new Promise<void>((resolve) => resolve());
      }
      schedulerRef.current.instance.suspendRefresh();

      allocationEvent.set('minutes', 0);
      schedulerRef.current.instance.eventStore.remove(allocationEvent);

      const { deleteAllocation, actionsMap } = bulkActionsMapActions();

      deleteAllocation(asAllocationPayload(allocationEvent));

      return bulkUpdate(actionsMap)
        .then(({ processSummaryUpdate, processTotalAllocationUpdate }) => {
          if (processSummaryUpdate) {
            updateAllocationAndSummary(
              allocationEvent.get('userId') != null
                ? {
                    [FetchableResourceType.UserList]: [
                      allocationEvent.get('userId'),
                    ],
                  }
                : {
                    [FetchableResourceType.RoleList]: [
                      allocationEvent.get('roleId'),
                    ],
                  },
              {
                fromDate: moment(allocationEvent.startDate),
                toDate: moment(allocationEvent.endDate),
              },
            );
          }

          if (processTotalAllocationUpdate) {
            updateTotalAllocation(
              allocationEvent.get('projectId'),
              allocationEvent.get('userId') != null
                ? {
                    type: ResourceType.User,
                    userId: allocationEvent.get('userId'),
                    roleId: allocationEvent.get('roleId'),
                    referenceTeamId: allocationEvent.get('referenceTeamId'),
                  }
                : {
                    teamId: allocationEvent.get('teamId'),
                    type: ResourceType.Team,
                  },
              allocationEvent.get('totalMinutes') * -1,
            );
          }
        })
        .finally(() => {
          schedulerRef.current.instance.resumeRefresh(true);
        });
    },
    [
      bulkUpdate,
      schedulerRef,
      updateAllocationAndSummary,
      updateTotalAllocation,
    ],
  );
};
